// src/components/DefaultPromptModal.js
import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  Box,
  Snackbar,
  Alert
} from '@mui/material';
import { supabase } from '../supabaseClient';

const DefaultPromptModal = ({ open, onClose, userId }) => {
  const [systemPrompt, setSystemPrompt] = useState('');
  const [directSystemPrompt, setDirectSystemPrompt] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [saveSuccess, setSaveSuccess] = useState('');
  const [openSnackbar, setOpenSnackbar] = useState(false);

  // Default prompts (should match backend defaults)
  const DEFAULT_SYSTEM_PROMPT = "You are an AI assistant that provides helpful and concise answers based solely on the provided document and conversation history. Use the documents and the conversation history to understand the user's question and provide a relevant and accurate answer. Do not include verbatim quotes or references to previous messages or questions in your answer. DO NOT MENTION ANY PREVIOUS CHAT IN YOUR ANSWER. Do not repeat any previous chat in your answer. DO NOT USE ANY EXTERNAL KNOWLEDGE OR YOUR PRETRAINED INFORMATION. DO NOT USE ANY INFORMATION NOT PRESENT IN THE DOCUMENT. Focus on providing concise, relevant, clear and accurate responses from the context. If you cannot find the answer in the context or conversation history, state that the information is not available and do not offer additional assistance or ask follow-up questions.";
  const DEFAULT_DIRECT_SYSTEM_PROMPT = "You are a helpful AI assistant. Answer the user's question directly.";

  // Fetch current default prompts from user_settings
  useEffect(() => {
    if (open && userId) {
      fetchUserSettings();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, userId]);

  const fetchUserSettings = async () => {
    setLoading(true);
    setError('');
    try {
      const { data, error: fetchError } = await supabase
        .from('user_settings')
        .select('system_prompt, direct_system_prompt')
        .eq('user_id', userId)
        .single();

      if (fetchError && fetchError.code !== 'PGRST116') { // PGRST116 = no rows found
        throw fetchError;
      }

      if (data) {
        setSystemPrompt(data.system_prompt || DEFAULT_SYSTEM_PROMPT);
        setDirectSystemPrompt(data.direct_system_prompt || DEFAULT_DIRECT_SYSTEM_PROMPT);
      } else {
        // No user settings found, use defaults
        setSystemPrompt(DEFAULT_SYSTEM_PROMPT);
        setDirectSystemPrompt(DEFAULT_DIRECT_SYSTEM_PROMPT);
      }
    } catch (e) {
      console.error('Error fetching user settings:', e);
      setError(`Failed to load settings: ${e.message || e}`);
      // Use defaults on error
      setSystemPrompt(DEFAULT_SYSTEM_PROMPT);
      setDirectSystemPrompt(DEFAULT_DIRECT_SYSTEM_PROMPT);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!userId) {
      setError('No userId available.');
      return;
    }

    setLoading(true);
    setError('');
    setSaveSuccess('');

    try {
      const { error: upsertError } = await supabase
        .from('user_settings')
        .upsert(
          {
            user_id: userId,
            system_prompt: systemPrompt || DEFAULT_SYSTEM_PROMPT,
            direct_system_prompt: directSystemPrompt || DEFAULT_DIRECT_SYSTEM_PROMPT
          },
          { onConflict: 'user_id' }
        );

      if (upsertError) {
        throw upsertError;
      }

      setSaveSuccess('Default system prompts saved successfully!');
      setOpenSnackbar(true);
      
      // Close modal after a short delay
      setTimeout(() => {
        onClose();
      }, 1500);
    } catch (e) {
      console.error('Error saving user settings:', e);
      setError(`Failed to save: ${e.message || e}`);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      setError('');
      setSaveSuccess('');
      onClose();
    }
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            minHeight: '500px',
            maxHeight: '80vh'
          }
        }}
      >
        <DialogTitle>
          <Typography variant="h6" component="div">
            View/Edit Default System Prompts
          </Typography>
          <Typography variant="caption" color="text.secondary">
            These prompts will be used as defaults for all new chat sessions.
          </Typography>
        </DialogTitle>
        <DialogContent dividers>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}
          
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600 }}>
              RAG Mode System Prompt
            </Typography>
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
              Used when answering questions based on your uploaded documents.
            </Typography>
            <TextField
              fullWidth
              multiline
              rows={8}
              value={systemPrompt}
              onChange={(e) => setSystemPrompt(e.target.value)}
              disabled={loading}
              placeholder={DEFAULT_SYSTEM_PROMPT}
              variant="outlined"
              sx={{ 
                '& .MuiInputBase-input': { fontSize: '0.875rem' },
                '& .MuiOutlinedInput-root': {
                  backgroundColor: '#fafafa'
                }
              }}
            />
          </Box>

          <Box>
            <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600 }}>
              Direct Mode System Prompt
            </Typography>
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
              Used when Direct Mode is enabled (no document context).
            </Typography>
            <TextField
              fullWidth
              multiline
              rows={4}
              value={directSystemPrompt}
              onChange={(e) => setDirectSystemPrompt(e.target.value)}
              disabled={loading}
              placeholder={DEFAULT_DIRECT_SYSTEM_PROMPT}
              variant="outlined"
              sx={{ 
                '& .MuiInputBase-input': { fontSize: '0.875rem' },
                '& .MuiOutlinedInput-root': {
                  backgroundColor: '#fafafa'
                }
              }}
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button 
            onClick={handleClose} 
            disabled={loading}
            color="inherit"
          >
            Cancel
          </Button>
          <Button 
            onClick={handleSave} 
            variant="contained" 
            disabled={loading || !userId}
          >
            {loading ? 'Saving...' : 'Save Default Prompts'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={openSnackbar}
        autoHideDuration={3000}
        onClose={() => setOpenSnackbar(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert 
          onClose={() => setOpenSnackbar(false)} 
          severity="success"
          sx={{ width: '100%' }}
        >
          {saveSuccess}
        </Alert>
      </Snackbar>
    </>
  );
};

export default DefaultPromptModal;






